import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MyEditor from "./AddNewTaskModalEditor";
import useOutsideClick from "../hooks/useOutsideClick";
import { toast } from "react-toastify";
import parse from "html-react-parser";

import {
  useGetTaskEnums,
  useGetTaskById,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
} from "../Controllers/TaskManagementController";
import { generateTimeOptions, timeToMinutes, calculateDuration } from "../Utils/timeEnum";

export default function AddNewTaskModal({
  show,
  onClose,
  taskId,
  taskData,
  isEditMode = false,
  initialLevel = "",
  onUpdate,
}) {
  const modalRef = useRef(null);
  useOutsideClick(modalRef, onClose);
  const queryClient = useQueryClient();

  // Extract numeric ID from taskId (e.g., "achieved-only-3875" -> 3875)
  const extractTaskId = (taskId) => {
    if (!taskId) return null;
    const parts = taskId.split("-");
    const numericId = parts[parts.length - 1];
    return parseInt(numericId, 10);
  };

  const invalidateTaskQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["taskManagement", "mitTasks"] });
    queryClient.invalidateQueries({ queryKey: ["taskManagement", "doLists"] });
  }, [queryClient]);

  // Form state
  const [selectedLevel, setSelectedLevel] = useState("Select Level");
  const [selectedPriority, setSelectedPriority] = useState("Select Priority");
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState("Select Start Time");
  const [endTime, setEndTime] = useState("Select End Time");
  // Updated subtasks to store objects with id and title
  const [subtasks, setSubtasks] = useState([{ id: null, title: "" }]);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [notes, setNotes] = useState("");

  // Recurring fields
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringDays, setRecurringDays] = useState([]);

  // UI state
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);
  const [isPriorityDropdownOpen, setIsPriorityDropdownOpen] = useState(false);
  const [isStartTimeDropdownOpen, setIsStartTimeDropdownOpen] = useState(false);
  const [isEndTimeDropdownOpen, setIsEndTimeDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Data state
  const [errors, setErrors] = useState({});

  // Delete dropdown state
  const [showDeleteDropdown, setShowDeleteDropdown] = useState(false);
  const deleteDropdownRef = useRef(null);

  // React Query hooks
  const { data: enumsData, isLoading: enumsLoading } = useGetTaskEnums(show);

  const { data: fetchedTaskData, isLoading: taskLoading } = useGetTaskById(
    isEditMode && taskId ? extractTaskId(taskId) : null,
    show && isEditMode
  );

  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const deleteTaskMutation = useDeleteTask();

  // Extract levels and priorities from enums data
  const levels = enumsData?.levels || [];
  const priorities = enumsData?.priorities || [];
  const timeOptions = generateTimeOptions();

  // Refs for dropdowns
  const levelDropdownRef = useRef(null);
  const priorityDropdownRef = useRef(null);
  const startTimeDropdownRef = useRef(null);
  const endTimeDropdownRef = useRef(null);

  // Reset form when modal closes
  const resetForm = useCallback(() => {
    setSelectedLevel("Select Level");
    setSelectedPriority("Select Priority");
    setSelectedDate(null);
    setStartTime("Select Start Time");
    setEndTime("Select End Time");
    setSubtasks([{ id: null, title: "" }]);
    setSelectedTitle("");
    setNotes("");
    setIsRecurring(false);
    setRecurringDays([]);
    setErrors({});
    setIsLevelDropdownOpen(false);
    setIsPriorityDropdownOpen(false);
    setIsStartTimeDropdownOpen(false);
    setIsEndTimeDropdownOpen(false);
  }, []);

  // Handle modal visibility changes
  useEffect(() => {
    if (!show) {
      resetForm();
    } else {
      if (!isEditMode) {
        setSelectedDate(new Date());
      }
      if (initialLevel && !isEditMode) {
        setSelectedLevel(initialLevel);
      }
    }
  }, [show, initialLevel, isEditMode, resetForm]);

  // Handle enums loading error
  useEffect(() => {
    if (enumsData === undefined && enumsLoading === false && show) {
      toast.error("Failed to load form options");
    }
  }, [enumsData, enumsLoading, show]);

  // Handle task data for edit mode
  useEffect(() => {
    if (fetchedTaskData?.task && isEditMode) {
      const task = fetchedTaskData.task;


      setSelectedTitle(task.title || "");
      setNotes(task.notes || "");
      setSelectedLevel(task.level || "Select Level");
      setSelectedPriority(task.priority || "Select Priority");
      setStartTime(task.start_time || "Select Start Time");
      setEndTime(task.end_time || "Select End Time");

      if (task.due_date) {
        setSelectedDate(new Date(task.due_date));
      }

      // Handle recurring fields in edit mode
      if (task.is_reoccuring) {
        setIsRecurring(true);
        setRecurringDays(task.reoccuring_days || []);
      } else {
        setIsRecurring(false);
        setRecurringDays([]);
      }

      // Updated to preserve subtask IDs
      const subtasksData = task.sub_tasks || [];
      if (subtasksData.length > 0) {
        setSubtasks(
          subtasksData.map((st) => ({
            id: st.id || null,
            title: st.title || "",
          }))
        );
      } else {
        setSubtasks([{ id: null, title: "" }]);
      }
    }
  }, [fetchedTaskData, isEditMode]);

  // Handle task loading error
  useEffect(() => {
    if (fetchedTaskData === undefined && taskLoading === false && show && isEditMode) {
      toast.error("Failed to load task details");
    }
  }, [fetchedTaskData, taskLoading, show, isEditMode]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        levelDropdownRef.current &&
        !levelDropdownRef.current.contains(event.target)
      ) {
        setIsLevelDropdownOpen(false);
      }
      if (
        priorityDropdownRef.current &&
        !priorityDropdownRef.current.contains(event.target)
      ) {
        setIsPriorityDropdownOpen(false);
      }
      if (
        startTimeDropdownRef.current &&
        !startTimeDropdownRef.current.contains(event.target)
      ) {
        setIsStartTimeDropdownOpen(false);
      }
      if (
        endTimeDropdownRef.current &&
        !endTimeDropdownRef.current.contains(event.target)
      ) {
        setIsEndTimeDropdownOpen(false);
      }
      if (
        deleteDropdownRef.current &&
        !deleteDropdownRef.current.contains(event.target)
      ) {
        setShowDeleteDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Utility functions
  const formatDateForPayload = (date) => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedTitle.trim()) newErrors.title = "Title is required.";
    if (selectedLevel === "Select Level")
      newErrors.level = "Please select a level.";
    if (selectedPriority === "Select Priority")
      newErrors.priority = "Please select a priority.";
    if (!selectedDate) newErrors.date = "Please select a date.";

    // Validate recurring fields only in create mode
    if (!isEditMode && isRecurring && recurringDays.length === 0) {
      newErrors.recurringDays = "Please select at least one day for recurring tasks.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Event handlers
  const handleSubtaskAdd = () => {
    setSubtasks([...subtasks, { id: null, title: "" }]);
  };

  const handleSubtaskChange = (index, value) => {
    const newSubtasks = [...subtasks];
    newSubtasks[index] = { ...newSubtasks[index], title: value };
    setSubtasks(newSubtasks);
  };

  const handleSubtaskRemove = (index) => {
    const updatedSubtasks = subtasks.filter((_, i) => i !== index);
    setSubtasks(updatedSubtasks);
  };

  const handleLevelSelect = (level) => {
    setSelectedLevel(level);
    setIsLevelDropdownOpen(false);
    if (errors.level) setErrors((prev) => ({ ...prev, level: undefined }));
  };

  const handlePrioritySelect = (priority) => {
    setSelectedPriority(priority);
    setIsPriorityDropdownOpen(false);
    if (errors.priority)
      setErrors((prev) => ({ ...prev, priority: undefined }));
  };

  const handleStartTimeSelect = (time) => {
    setStartTime(time);
    setIsStartTimeDropdownOpen(false);

    // If end time is selected and is before start time, reset end time
    if (endTime !== "Select End Time" && timeToMinutes(time) >= timeToMinutes(endTime)) {
      setEndTime("Select End Time");
    }

    if (errors.startTime) setErrors((prev) => ({ ...prev, startTime: undefined }));
  };

  const handleEndTimeSelect = (time) => {
    setEndTime(time);
    setIsEndTimeDropdownOpen(false);
    if (errors.endTime) setErrors((prev) => ({ ...prev, endTime: undefined }));
  };

  const handleNotesChange = useCallback(
    (value) => {
      setNotes(value);
      if (errors.notes) {
        setErrors((prev) => ({ ...prev, notes: undefined }));
      }
    },
    [errors.notes]
  );

  const handleSubmit = async () => {
    if (isLoading || createTaskMutation.isPending || updateTaskMutation.isPending || !validateForm()) return;

    setIsLoading(true);

    // Updated payload to include subtask IDs and time fields
    const payload = {
      title: selectedTitle,
      notes: notes,
      level: selectedLevel !== "Select Level" ? selectedLevel : null,
      priority:
        selectedPriority !== "Select Priority" ? selectedPriority : null,
      due_date: selectedDate ? formatDateForPayload(selectedDate) : null,
      start_time: startTime !== "Select Start Time" ? startTime : null,
      end_time: endTime !== "Select End Time" ? endTime : null,
      sub_tasks: subtasks
        .filter((task) => task.title.trim() !== "")
        .map((task) => {
          const subtaskPayload = { title: task.title };
          // Include ID only if it exists (for existing subtasks)
          if (task.id) {
            subtaskPayload.id = task.id;
          }
          return subtaskPayload;
        }),
      // Add recurring fields only in create mode
      ...(isEditMode ? {} : {
        is_reoccuring: isRecurring,
        reoccuring_days: isRecurring ? recurringDays : []
      })
    };

    try {
      if (isEditMode && taskId) {
        const id = extractTaskId(taskId);
        await updateTaskMutation.mutateAsync({ taskId: id, payload });
      } else {
        await createTaskMutation.mutateAsync(payload);
      }

      toast.success(`Task ${isEditMode ? "updated" : "created"} successfully!`);
      invalidateTaskQueries();
      onClose();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} task:`,
        error
      );

      const errorMessage = error?.data?.message;
      toast.error(
        errorMessage ||
        `Something went wrong while ${isEditMode ? "updating" : "creating"
        } the task.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (deleteScope = null) => {

    if (!taskId || isLoading || deleteTaskMutation.isPending) return;

    // Check if task is recurring
    if (fetchedTaskData?.task?.is_reoccuring && !deleteScope) {

      setShowDeleteDropdown(true);
      return;
    }


    // For non-recurring tasks or when scope is provided, delete directly
    await performDelete(deleteScope);
  };

  const performDelete = async (deleteScope = null) => {

    setIsLoading(true);
    try {
      const id = extractTaskId(taskId);
      let payload = {};

      // Add delete scope if provided (only for recurring tasks)
      if (deleteScope === 'all') {
        payload.delete_all = true;
      } else if (deleteScope === 'this_and_future') {
        payload.delete_this_and_future = true;
      } else if (deleteScope === 'this_only') {
        payload.delete_this_only = true;
      }



      toast.success("Task deleted successfully!");
      invalidateTaskQueries();
      onClose();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error(error?.message || error?.response?.data?.message || "Failed to delete task");
    } finally {
      setIsLoading(false);
      setShowDeleteDropdown(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal add-task-modal fade show"
        id="addNewTaskModal"
        tabIndex="-1"
        aria-labelledby="addNewTaskModalLabel"
        aria-hidden="true"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content" ref={modalRef}>
            <div className="modal-header">
              <h1 className="modal-title text-heading-h3">
                {isEditMode ? "Edit Target" : "Target"}
              </h1>
            </div>
            <hr className="modal-hr" />
            <div className="modal-body">
              {(isLoading || (isEditMode && taskLoading)) ? (
                <div className="text-center py-4">
                  <span
                    className="spinner-border spinner-border-lg"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <div className="mt-2">Loading task details...</div>
                </div>
              ) : (
                <form
                  className="add-task-form"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <div className="row">
                    {/* Title Field */}
                    <div className="col-md-12">
                      <div className="textfield-1">
                        <input
                          className="text-body-b2"
                          type="text"
                          placeholder=""
                          value={parse(selectedTitle.replace(/<[^>]*>/g, ""))}
                          onChange={(e) => {
                            setSelectedTitle(e.target.value);
                            if (errors.title)
                              setErrors((prev) => ({
                                ...prev,
                                title: undefined,
                              }));
                          }}
                        />
                        <label
                          className="text-body-b2"
                          style={{ background: "#132534" }}
                        >
                          Title
                        </label>
                      </div>
                      {errors.title && (
                        <div className="text-danger mt-1 text-small-s3">
                          {errors.title}
                        </div>
                      )}
                    </div>

                    {/* Level Dropdown */}
                    <div className="col-md-6">
                      <div
                        className="textfield-1 position-relative"
                        ref={levelDropdownRef}
                      >
                        <input
                          type="text"
                          className="custom-select for-level"
                          placeholder=""
                          value={selectedLevel}
                          onClick={() =>
                            setIsLevelDropdownOpen(!isLevelDropdownOpen)
                          }
                          readOnly
                        />
                        {isLevelDropdownOpen && (
                          <div
                            className="custom-select-dropdown for-level"
                            style={{
                              zIndex: 10,
                              display: "block",
                              position: "absolute",
                              width: "100%",
                              background: "#192f41",
                              border: "1px solid #2d3c48",
                              borderRadius: "20px",
                              padding: "16px 20px",
                              boxShadow: "2px 4px 6px 0px #0000002e",
                            }}
                          >
                            <h4 className="text-body-b2 mb-0">Select Level</h4>
                            <hr />
                            {levels.map((level) => (
                              <span
                                key={level}
                                className={`text-body-b2 mb-3 d-block ${selectedLevel === level ? "active" : ""
                                  }`}
                                onClick={() => handleLevelSelect(level)}
                                style={{ cursor: "pointer" }}
                              >
                                {level}
                              </span>
                            ))}
                          </div>
                        )}
                        <label
                          className="text-body-b2"
                          style={{ background: "#132534" }}
                        >
                          Level
                        </label>
                      </div>
                      {errors.level && (
                        <div className="text-danger mt-1 text-small-s3">
                          {errors.level}
                        </div>
                      )}
                    </div>

                    {/* Priority Dropdown */}
                    <div className="col-md-6">
                      <div
                        className="textfield-1 position-relative"
                        ref={priorityDropdownRef}
                      >
                        <input
                          type="text"
                          className="custom-select for-priority"
                          placeholder=""
                          value={selectedPriority}
                          onClick={() =>
                            setIsPriorityDropdownOpen(!isPriorityDropdownOpen)
                          }
                          readOnly
                        />
                        {isPriorityDropdownOpen && (
                          <div
                            className="custom-select-dropdown for-priority"
                            style={{
                              zIndex: 10,
                              display: "block",
                              position: "absolute",
                              width: "100%",
                              background: "#192f41",
                              border: "1px solid #2d3c48",
                              borderRadius: "20px",
                              padding: "16px 20px",
                              boxShadow: "2px 4px 6px 0px #0000002e",
                            }}
                          >
                            <h4 className="text-body-b2 mb-0">Select Priority</h4>
                            <hr />
                            {priorities.map((priority) => (
                              <span
                                key={priority}
                                className={`text-body-b2 mb-3 d-block ${selectedPriority === priority ? "active" : ""
                                  }`}
                                onClick={() => handlePrioritySelect(priority)}
                                style={{ cursor: "pointer" }}
                              >
                                {priority}
                              </span>
                            ))}
                          </div>
                        )}
                        <label
                          className="text-body-b2"
                          style={{ background: "#132534" }}
                        >
                          Priority
                        </label>
                      </div>
                      {errors.priority && (
                        <div className="text-danger mt-1 text-small-s3">
                          {errors.priority}
                        </div>
                      )}
                    </div>

                    {/* Date Picker */}
                    <div className="col-md-12">
                      <div className="textfield-1 position-relative">
                        <DatePicker
                          selected={selectedDate}
                          onChange={(date) => {
                            setSelectedDate(date);
                            if (errors.date)
                              setErrors((prev) => ({ ...prev, date: undefined }));
                          }}
                          onChangeRaw={(e) => {
                            const inputValue = e.target.value;
                            const parsedDate = new Date(inputValue);

                            if (
                              !isNaN(parsedDate.getTime()) &&
                              inputValue.trim() !== ""
                            ) {
                              setSelectedDate(parsedDate);
                              if (errors.date)
                                setErrors((prev) => ({
                                  ...prev,
                                  date: undefined,
                                }));
                            }
                          }}
                          onFocus={(e) => e.target.blur()}
                          dateFormat="MMMM d, yyyy"
                          className="text-body-b2 date-input"
                          placeholderText="Select or Type Date"
                          isClearable={true}
                          autoComplete="off"
                        />
                        <label
                          className="text-body-b2"
                          style={{
                            background: "#132534",
                            position: "absolute",
                            top: "-12px",
                            left: "12px",
                            padding: "0 5px",
                            fontSize: "10px",
                            color: "#e6ecf1",
                          }}
                        >
                          Date
                        </label>
                        <img
                          src="/icons/calender-icon.svg"
                          alt="calendar icon"
                          style={{
                            position: "absolute",
                            right: "30px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: "16px",
                            pointerEvents: "none",
                          }}
                        />
                      </div>
                      {errors.date && (
                        <div className="text-danger mt-1 text-small-s3">
                          {errors.date}
                        </div>
                      )}
                    </div>

                    {/* Start Time and End Time Fields */}
                    <div className="col-md-6">
                      <div
                        className="textfield-1 position-relative"
                        ref={startTimeDropdownRef}
                      >
                        <input
                          type="text"
                          className="custom-select for-start-time"
                          placeholder=""
                          value={startTime}
                          onClick={() =>
                            setIsStartTimeDropdownOpen(!isStartTimeDropdownOpen)
                          }
                          readOnly
                        />
                        {isStartTimeDropdownOpen && (
                          <div
                            className="custom-select-dropdown for-start-time"
                            style={{
                              zIndex: 10,
                              display: "block",
                              position: "absolute",
                              width: "100%",
                              background: "#192f41",
                              border: "1px solid #2d3c48",
                              borderRadius: "20px",
                              padding: "16px 20px",
                              boxShadow: "2px 4px 6px 0px #0000002e",
                              maxHeight: "200px",
                              overflowY: "auto",
                            }}
                          >
                            <h4 className="text-body-b2 mb-0">Select Start Time</h4>
                            <hr />
                            {timeOptions.map((time) => (
                              <span
                                key={time}
                                className={`text-body-b2 mb-2 d-block ${startTime === time ? "active" : ""
                                  }`}
                                onClick={() => handleStartTimeSelect(time)}
                                style={{ cursor: "pointer" }}
                              >
                                {time}
                              </span>
                            ))}
                          </div>
                        )}
                        <label
                          className="text-body-b2"
                          style={{ background: "#132534" }}
                        >
                          Start Time
                        </label>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div
                        className="textfield-1 position-relative"
                        ref={endTimeDropdownRef}
                      >
                        <input
                          type="text"
                          className="custom-select for-end-time"
                          placeholder=""
                          value={
                            endTime !== "Select End Time" && startTime !== "Select Start Time"
                              ? `${endTime} (${calculateDuration(startTime, endTime) || ""})`
                              : endTime
                          }
                          onClick={() =>
                            setIsEndTimeDropdownOpen(!isEndTimeDropdownOpen)
                          }
                          readOnly
                        />
                        {isEndTimeDropdownOpen && (
                          <div
                            className="custom-select-dropdown for-end-time"
                            style={{
                              zIndex: 10,
                              display: "block",
                              position: "absolute",
                              width: "100%",
                              background: "#192f41",
                              border: "1px solid #2d3c48",
                              borderRadius: "20px",
                              padding: "16px 20px",
                              boxShadow: "2px 4px 6px 0px #0000002e",
                              maxHeight: "200px",
                              overflowY: "auto",
                            }}
                          >
                            <h4 className="text-body-b2 mb-0">Select End Time</h4>
                            <hr />
                            {timeOptions
                              .filter((time) => {
                                // Filter out times that are before or equal to start time
                                if (startTime === "Select Start Time") return true;
                                return timeToMinutes(time) > timeToMinutes(startTime);
                              })
                              .map((time) => (
                                <span
                                  key={time}
                                  className={`text-body-b2 mb-2 d-block `}
                                  onClick={() => handleEndTimeSelect(time)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {time}
                                  {startTime !== "Select Start Time" && (
                                    <span className="text-white ms-2">
                                      ({calculateDuration(startTime, time)})
                                    </span>
                                  )}
                                </span>
                              ))}
                          </div>
                        )}
                        <label
                          className="text-body-b2"
                          style={{ background: "#132534" }}
                        >
                          End Time
                        </label>
                      </div>
                    </div>

                    {/* Recurring Fields - Show in create mode and edit mode if task is recurring */}
                    {(!isEditMode || (isEditMode && fetchedTaskData?.task?.is_reoccuring)) && (
                      <>
                        {/* Recurring Checkbox */}
                        <div className="col-md-12">
                          <div className="d-flex align-items-center mb-3">
                            <input
                              type="checkbox"
                              id="recurring-checkbox"
                              checked={isRecurring}
                              onChange={(e) => !isEditMode && setIsRecurring(e.target.checked)}
                              disabled={isEditMode}
                              className="me-3"
                              style={{
                                transform: "scale(1.2)",
                                opacity: isEditMode ? 0.6 : 1,
                                cursor: "pointer"
                              }}
                            />
                            <label
                              htmlFor="recurring-checkbox"
                              className="text-body-b2"
                              style={{
                                color: isEditMode ? "#888" : "#e6ecf1",
                                cursor: "pointer"
                              }}
                            >
                              Recurring
                            </label>
                          </div>
                        </div>

                        {/* Recurring Days - Only show when recurring is checked */}
                        {isRecurring && (
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="text-body-b2 mb-3 d-block" style={{ color: isEditMode ? "#888" : "#d0d8df" }}>
                                Repeat On
                              </label>
                              <div className="d-flex gap-2 flex-wrap">
                                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
                                  <button
                                    key={day}
                                    type="button"
                                    disabled={isEditMode}
                                    className={`btn btn-sm ${recurringDays.includes(day)
                                      ? 'btn-primary'
                                      : 'btn-outline-secondary'
                                      }`}
                                    onClick={() => {
                                      if (!isEditMode) {
                                        if (recurringDays.includes(day)) {
                                          setRecurringDays(recurringDays.filter(d => d !== day));
                                        } else {
                                          setRecurringDays([...recurringDays, day]);
                                        }
                                      }
                                    }}
                                    style={{
                                      minWidth: '60px',
                                      fontSize: '12px',
                                      padding: '4px 8px',
                                      border: recurringDays.includes(day)
                                        ? '1px solid #007bff'
                                        : '1px solid #6c757d',
                                      backgroundColor: recurringDays.includes(day)
                                        ? '#007bff'
                                        : 'transparent',
                                      color: recurringDays.includes(day)
                                        ? '#fff'
                                        : '#6c757d',
                                      opacity: isEditMode ? 0.6 : 1,
                                      cursor: "pointer"
                                    }}
                                  >
                                    {day.substring(0, 3)}
                                  </button>
                                ))}
                              </div>
                              {errors.recurringDays && (
                                <div className="text-danger mt-1 text-small-s3">
                                  {errors.recurringDays}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Add Subtask Button */}
                    <div className="col-md-12">
                      <button
                        type="button"
                        className="btn-1 btn-1-basic text-body-b3 subtask-add-btn"
                        style={{
                          height: "40px",
                          width: "100%",
                          color: "#e6ecf1",
                        }}
                        onClick={handleSubtaskAdd}
                      >
                        Add Subtask
                      </button>
                    </div>

                    {/* Subtasks */}
                    {subtasks.map((subtask, index) => (
                      <div
                        key={index}
                        className="col-md-12 d-flex align-items-center"
                      >
                        <div className="textfield-1 flex-grow-1">
                          <input
                            className="text-body-b2"
                            type="text"
                            placeholder=""
                            value={subtask.title}
                            onChange={(e) =>
                              handleSubtaskChange(index, e.target.value)
                            }
                          />
                          <label
                            className="text-body-b2"
                            style={{ background: "#132534" }}
                          >
                            Subtask {index + 1}
                          </label>
                        </div>
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => handleSubtaskRemove(index)}
                            style={{
                              color: "red",
                              marginLeft: "10px",
                              background: "transparent",
                              border: "none",
                              fontSize: "24px",
                              lineHeight: "1",
                            }}
                            title="Remove subtask"
                          >
                            &minus;
                          </button>
                        )}
                      </div>
                    ))}

                    {/* Notes Editor */}
                    <div className="col-md-12">
                      <div className="notes-text-editor for-task-management">
                        <label
                          className="text-small-s2 px-1"
                          style={{ color: "#d0d8df" }}
                        >
                          Notes
                        </label>
                        <MyEditor value={notes} onChange={handleNotesChange} />
                      </div>
                    </div>
                  </div>
                </form>
              )}

              {/* Action Buttons */}
              <div className="d-flex gap-3 mt-4 justify-content-center">
                <button
                  className="btn-1 btn-1-primary px-3 text-body-b1"
                  style={{ width: "182px" }}
                  onClick={handleSubmit}
                  disabled={isLoading || createTaskMutation.isPending || updateTaskMutation.isPending}
                >
                  {(isLoading || createTaskMutation.isPending || updateTaskMutation.isPending) ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : (
                    "Save"
                  )}
                </button>
                {isEditMode ? (
                  <div className="position-relative" ref={deleteDropdownRef}>
                    <button
                      className="btn-1 btn-1-primary px-3 text-body-b1"
                      style={{ width: "182px", background: "#eb5757" }}
                      onClick={() => setShowDeleteDropdown(!showDeleteDropdown)}
                      disabled={isLoading || deleteTaskMutation.isPending}
                    >
                      Delete
                    </button>
                    {showDeleteDropdown && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: "0",
                          right: "0",
                          backgroundColor: "#192f41",
                          border: "1px solid #2d3c48",
                          borderRadius: "8px",
                          padding: "8px 0",
                          marginTop: "4px",
                          zIndex: 1000,
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)"
                        }}
                      >
                        {fetchedTaskData?.task?.is_reoccuring ? (
                          <>
                            <button
                              className="btn w-100 text-start px-3 py-2"
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#e6ecf1",
                                fontSize: "14px"
                              }}
                              onClick={() => handleDelete('this_only')}
                            >
                              Delete This Only
                            </button>
                            <button
                              className="btn w-100 text-start px-3 py-2"
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#e6ecf1",
                                fontSize: "14px"
                              }}
                              onClick={() => handleDelete('this_and_future')}
                            >
                              Delete This and Future
                            </button>
                            <button
                              className="btn w-100 text-start px-3 py-2"
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#eb5757",
                                fontSize: "14px"
                              }}
                              onClick={() => handleDelete('all')}
                            >
                              Delete All
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn w-100 text-start px-3 py-2"
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "#e6ecf1",
                              fontSize: "14px"
                            }}
                            onClick={() => handleDelete()}
                          >
                            Delete Task
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px", background: "#eb5757" }}
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
}

